import { useState, useCallback } from 'react';
import * as api from '../services/api';

export function useIngest() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const createEntity = useCallback(async (entityData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await api.createEntity(entityData);
      setLastResult(result);
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createRelationship = useCallback(async (relationshipData) => {
    setLoading(true);
    setError(null);

    try {
      const result = await api.createRelationship(relationshipData);
      setLastResult(result);
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const extractFromText = useCallback(async (content, autoCreate = false) => {
    setLoading(true);
    setError(null);

    try {
      // Simulate extraction with mock data
      await new Promise(resolve => setTimeout(resolve, 1500));

      const mockResult = {
        extracted: {
          entities: [
            { id: 'svc-new-service', name: 'new-service', type: 'SERVICE', description: 'Extracted service', confidence: 0.89 },
          ],
          relationships: [
            { source: 'svc-new-service', target: 'svc-auth', type: 'DEPENDS_ON', confidence: 0.75 },
          ],
        },
        auto_create: autoCreate,
        entities_created: autoCreate ? 1 : 0,
        relationships_created: autoCreate ? 1 : 0,
        processing_time_ms: 1234,
      };

      setLastResult(mockResult);
      return mockResult;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const submitInsight = useCallback(async (content, autoCreate = true) => {
    setLoading(true);
    setError(null);

    try {
      // Simulate insight submission
      await new Promise(resolve => setTimeout(resolve, 1000));

      const mockResult = {
        extracted: {
          entities: [],
          relationships: [],
        },
        auto_create: autoCreate,
        entities_created: 0,
        relationships_created: 0,
        processing_time_ms: 800,
      };

      setLastResult(mockResult);
      return mockResult;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setError(null);
    setLastResult(null);
  }, []);

  return {
    loading,
    error,
    lastResult,
    createEntity,
    createRelationship,
    extractFromText,
    submitInsight,
    clear,
  };
}

export function useBulkImport() {
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const uploadFiles = useCallback(async (files) => {
    setLoading(true);
    setError(null);

    try {
      // Simulate upload
      await new Promise(resolve => setTimeout(resolve, 500));

      const mockJobId = `job-${Date.now().toString(36)}`;
      setJobId(mockJobId);
      setStatus({ status: 'PENDING', total_files: files.length });

      return { job_id: mockJobId };
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const processJob = useCallback(async () => {
    if (!jobId) return null;

    setLoading(true);
    setError(null);

    try {
      // Simulate processing
      setStatus(prev => ({ ...prev, status: 'PROCESSING' }));
      await new Promise(resolve => setTimeout(resolve, 2000));

      const result = {
        job_id: jobId,
        status: 'COMPLETED',
        processed_files: status?.total_files || 1,
        entities_created: 5,
        relationships_created: 3,
        errors: [],
      };

      setStatus(result);
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [jobId, status]);

  const checkStatus = useCallback(async () => {
    if (!jobId) return null;

    try {
      const result = await api.getBulkJobStatus(jobId);
      setStatus(result);
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [jobId]);

  const reset = useCallback(() => {
    setJobId(null);
    setStatus(null);
    setError(null);
  }, []);

  return {
    jobId,
    status,
    loading,
    error,
    uploadFiles,
    processJob,
    checkStatus,
    reset,
  };
}
